import { MERCHANT_ROLES, MERCHANT_USER_STATUSES } from "@price-rescue/api-contracts";
import {
  MERCHANT_ROLE_APP_ADMIN,
  MERCHANT_ROLE_PRICING_MANAGER,
} from "@price-rescue/domain/statuses";
import type { MerchantRole, MerchantUserStatus } from "../../types.js";

export type TeamUserAction = "ASSIGN_ROLE" | "SUSPEND" | "REACTIVATE";
export type TeamUserStatusFilter = MerchantUserStatus | "";

export interface TeamAccessFilters {
  cursor?: string | undefined;
  limit?: number | undefined;
  role?: MerchantRole | "" | undefined;
  status?: TeamUserStatusFilter | undefined;
}

export interface TeamUser {
  userId: string;
  platformUserId: string;
  status: MerchantUserStatus;
  role?: MerchantRole;
  email?: string;
  displayName?: string;
  grantedBy?: string;
  statusReason?: string;
  firstSeenAt: string;
  lastSeenAt?: string;
  createdAt: string;
  updatedAt: string;
  allowedActions: TeamUserAction[];
  auditGuidance: string;
  platformAccessGuidance: string;
}

export interface TeamAccessPage {
  users: TeamUser[];
  nextCursor: string | null;
}

export interface RoleAssignmentDraft {
  role: MerchantRole;
  reason: string;
}

export interface StatusChangeDraft {
  status: "ACTIVE" | "SUSPENDED";
  reason: string;
  confirmation: string;
}

export const TEAM_ROLES: MerchantRole[] = [...MERCHANT_ROLES];
export const ASSIGNABLE_TEAM_ROLES: MerchantRole[] = [
  MERCHANT_ROLE_APP_ADMIN,
  MERCHANT_ROLE_PRICING_MANAGER,
];
export const TEAM_STATUSES: TeamUserStatusFilter[] = ["", ...MERCHANT_USER_STATUSES];
export const ROLE_GUIDANCE =
  "Owner is set from Shopify's store-owner identity. Owner and App Admin have full Price Rescue access. Pricing Manager can handle operational pricing without Store Configuration or Administration.";

export function defaultTeamAccessFilters(): TeamAccessFilters {
  return { limit: 25, role: "", status: "" };
}

export function defaultRoleAssignmentDraft(user?: TeamUser): RoleAssignmentDraft {
  return {
    role: user?.role ?? MERCHANT_ROLE_PRICING_MANAGER,
    reason: "",
  };
}

export function defaultStatusChangeDraft(status: "ACTIVE" | "SUSPENDED"): StatusChangeDraft {
  return {
    status,
    reason: "",
    confirmation: "",
  };
}

export function userDisplayName(user: TeamUser): string {
  return user.displayName ?? user.email ?? user.platformUserId;
}

export function userRoleLabel(role: MerchantRole | undefined): string {
  if (role === undefined) return "Unassigned";
  return role.split("_").map(titleCase).join(" ");
}

export function statusLabel(status: MerchantUserStatus | ""): string {
  if (status === "") return "All statuses";
  return status.split("_").map(titleCase).join(" ");
}

export function validateRoleAssignmentDraft(draft: RoleAssignmentDraft): string[] {
  return [draft.reason.trim() === "" ? "A reason is required for role changes." : undefined].filter(
    (issue): issue is string => issue !== undefined,
  );
}

export function validateStatusChangeDraft(user: TeamUser, draft: StatusChangeDraft): string[] {
  return [
    draft.reason.trim() === "" ? "A reason is required for status changes." : undefined,
    draft.confirmation !== userDisplayName(user)
      ? `Type ${userDisplayName(user)} to confirm this change.`
      : undefined,
  ].filter((issue): issue is string => issue !== undefined);
}

export function statusActionLabel(status: "ACTIVE" | "SUSPENDED"): string {
  switch (status) {
    case "ACTIVE":
      return "Reactivate";
    case "SUSPENDED":
      return "Suspend";
  }
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}
